import { serialize } from "object-to-formdata"
import { PRICING_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { AFFECTS, APPLIES_ON_PRIVATE } from "../constants/pricingConstants"
import { daysToApi, timesTo24h } from "./scheduleApiFormat"

/**
 * Converts form values → serialized API payload for steps that use pure form state.
 *
 * Steps with non-form local state (scheduling, media, location, resources, digital_waiver)
 * register their own save handlers via stepSaveHandlersRef — this file only handles
 * the steps where form values alone are sufficient.
 *
 * IMPORTANT: API keys here must match existing backend contract exactly.
 */

/**
 * Details step payload.
 * Maps multi-value string arrays to the _attributes format expected by Rails.
 * selectedLanguages / selectedSkillLevels come from ExperienceDetails callbacks
 * (those components don't use Form.Item onChange, they use prop callbacks).
 *
 * @param {object} formValues
 * @param {object} options
 */
export const buildDetailsPayload = (formValues, { experienceType } = {}) => {
  const { categories: rawCategories, included_items = [], bring_items = [], languages = [], skill_levels = [], sub_categories = [], add_on_ids = [], ...rest } = formValues

  return serialize(
    {
      trip: {
        ...rest,
        ...(experienceType ? { experience_type: experienceType } : {}),
        language_ids: languages,
        skill_level_ids: skill_levels,
        category_id: rawCategories ?? null,
        subcategory_ids: sub_categories,
        included_item_ids: included_items,
        bring_item_ids: bring_items,
        add_on_ids
      }
    },
    { allowEmptyArrays: true }
  )
}

/**
 * Preview step payload — marks the wizard as complete.
 */
export const buildPreviewPayload = () =>
  serialize({ trip: { wizard_step: "preview", wizard_completed: true } }, { allowEmptyArrays: true })

/**
 * Publish payload — mirrors v1, which publishes by setting status: "published".
 */
export const buildPublishPayload = () =>
  serialize({ trip: { status: "published", wizard_completed: true } }, { allowEmptyArrays: true })

/**
 * Multi-day schedule: a trip keeps exactly one schedule block (all start days +
 * one shared start time). Any other existing (single-day) schedules are flagged
 * _destroy so the backend doesn't return both shapes after a single→multi switch.
 */
const buildMultiDaySchedules = (formValues) => {
  const scheduleId = formValues.multi_day_schedule_id
  const multiDayBlock = {
    ...(isServerId(scheduleId) ? { id: scheduleId } : {}),
    schedule_type: "multi_day",
    name: null,
    start_time: formValues.multi_day_start_time || null,
    end_time: null,
    increment_minutes: null,
    days: daysToApi(formValues.multi_day_start_days || []).filter(Boolean),
  }
  const destroyOthers = (formValues.availability_schedules || [])
    .map((schedule) => schedule.id)
    .filter((id) => isServerId(id) && id !== scheduleId)
    .map((id) => ({ id, _destroy: true }))
  return [multiDayBlock, ...destroyOthers]
}

/**
 * True for a multi-day schedule block. Prefers the backend's explicit schedule_type;
 * falls back to shape (start_time only — no end time / increment, which map to
 * to_time / interval on the form record) for older records that predate the field.
 * Used to spot multi-day remnants left in availability_schedules after a multi→single switch.
 */
const isMultiDayShape = (schedule) =>
  schedule.schedule_type === "multi_day" ||
  (schedule.schedule_type == null && schedule.to_time == null && schedule.interval == null)

/**
 * Single-day schedules: backend wants schedules in indexed form ([i][days][j])
 * for everything EXCEPT disabled_times (un-indexed [] append format).
 */
const buildSingleDaySchedules = (formValues) =>
  (formValues.availability_schedules || [])
    // A never-saved schedule that was deleted has nothing to remove — skip it.
    .filter((schedule) => !(schedule._destroy && !isServerId(schedule.id)))
    .map((schedule) => {
      // Persisted schedule flagged for deletion: send only id + _destroy.
      if (schedule._destroy && isServerId(schedule.id)) {
        return { id: schedule.id, _destroy: true }
      }
      // A multi-day remnant (start_time only) must not be re-sent as a single-day
      // rule. Destroy it if it was persisted; drop it locally if it's a temp record.
      if (isMultiDayShape(schedule)) {
        return isServerId(schedule.id) ? { id: schedule.id, _destroy: true } : null
      }
      return {
        ...(isServerId(schedule.id) ? { id: schedule.id } : {}),
        schedule_type: "single_day",
        name: schedule.name ?? null,
        start_time: schedule.from_time ?? null,
        end_time: schedule.to_time ?? null,
        increment_minutes: schedule.interval ?? null,
        // Filter empties so serialize doesn't emit a trailing days[N]= entry.
        days: daysToApi(schedule.days ?? []).filter(Boolean),
        // disabled_times appended manually below (backend wants [] format).
      }
    })
    // Drop temp-id multi-day remnants (they map to null above).
    .filter(Boolean)

/**
 * Scheduling step payload builder (used inside SchedulingStep's registered handler).
 * Extracted here for testability and co-location with other payload builders.
 */
export const buildSchedulingPayload = (formValues, { isSeasonalExperience, timeBlocks, isTicketed = false }) => {
  const updateData = {
    wizard_step: "schedule",
    time_zone: formValues.time_zone,
  }

  if (isTicketed) {
    updateData.duration_value = formValues.duration_value
    updateData.duration_unit = formValues.duration_unit
    updateData.cut_off_value = formValues.cut_off_value
    updateData.cut_off_unit = formValues.cut_off_unit
    updateData.low_availability_enabled = formValues.low_availability_enabled
    updateData.low_availability_threshold = formValues.low_availability_threshold
    // time_slots entries have { id, label, starts_at } — send starts_at to backend
    updateData.scheduled_slots_attributes = (formValues.time_slots || []).map(({ id, starts_at, _destroy }) => ({
      ...(isServerId(id) ? { id } : {}),
      starts_at,
      ...(_destroy ? { _destroy: true } : {})
    }))
  } else {
    updateData.duration_type = formValues.duration_type
    if (formValues.duration_type === "multi_day") {
      updateData.duration_value = formValues.number_of_days
    }
    updateData.cut_off_value = formValues.cut_off_value
    updateData.cut_off_unit = formValues.cut_off_unit
    updateData.seasonal = isSeasonalExperience

    if (isSeasonalExperience) {
      updateData.recurring_yearly = formValues.recurringYearly

      const dateRanges = timeBlocks
        .map((blockIndex) => {
          const start = formValues[`seasonStart_${blockIndex}`]
          const end = formValues[`seasonEnd_${blockIndex}`]
          if (start && end) {
            return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") }
          }
          return null
        })
        .filter(Boolean)

      if (dateRanges.length > 0) {
        updateData.available_date_ranges = dateRanges
      }
    }

    updateData.trip_availability_schedules_attributes =
      formValues.duration_type === "multi_day"
        ? buildMultiDaySchedules(formValues)
        : buildSingleDaySchedules(formValues)

    // schedule_overrides is out of scope (its UI is commented out), so we don't
    // send schedule_overrides_attributes — an empty array could clear existing
    // overrides backend-side. Re-add this when the override feature is enabled.
  }

  // indices: true so nested object arrays (available_date_ranges,
  // trip_availability_schedules_attributes) are emitted with explicit indices,
  // which the backend expects for grouping ([0][start], [i][days][j]).
  const formData = serialize({ trip: updateData }, { allowEmptyArrays: true, indices: true })

  if (!isTicketed && formValues.duration_type !== "multi_day") {
    // Append disabled_times per schedule using the un-indexed [] format the
    // backend wants. Emit an empty disabled_times[]= when there are none, so the
    // attribute is always present ("Empty value = no disabled times").
    // Multi-day sends a single schedule with no disabled_times, so skip it there.
    // Mirror buildSingleDaySchedules exactly so these indices line up with the
    // serialized array: temp deletes and temp multi-day remnants are dropped (no
    // index); persisted deletes / multi-day remnants keep an index but emit nothing.
    const schedules = (formValues.availability_schedules || [])
      .filter((schedule) => !(schedule._destroy && !isServerId(schedule.id)))
      .filter((schedule) => !(isMultiDayShape(schedule) && !isServerId(schedule.id)))

    schedules.forEach((schedule, index) => {
      // Deletion entries and persisted multi-day remnants carry no disabled_times.
      if (schedule._destroy && isServerId(schedule.id)) return
      if (isMultiDayShape(schedule)) return

      const prefix = `trip[trip_availability_schedules_attributes][${index}][disabled_times][]`
      const times = timesTo24h(schedule.disabled_times ?? [])
      if (times.length === 0) {
        formData.append(prefix, "")
      } else {
        times.forEach((time) => formData.append(prefix, time))
      }
    })
  }

  return formData
}

/**
 * Location step payload builder (used inside LocationStep's registered handler).
 */
export const buildLocationPayload = ({ additional_directions, meetingLatLng, coordinates }) =>
  serialize(
    {
      trip: {
        wizard_step: "location",
        additional_directions: additional_directions || "",
        latitude: coordinates?.lat,
        longitude: coordinates?.lng,
        address: meetingLatLng
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Resources step payload builder (used inside ResourcesStep's registered handler).
 */
export const buildResourcesPayload = ({ selectedEquipment, selectedCrewMembers, autoAssignEquipment }) =>
  serialize(
    {
      trip: {
        wizard_step: "boats",
        boat_ids: selectedEquipment.filter((id) => id !== "all").map(Number),
        assigned_staff_ids: selectedCrewMembers.filter((id) => id !== "all").map(Number),
        auto_assign_equipment: autoAssignEquipment
      }
    },
    { allowEmptyArrays: true }
  )

/**
 * Media step payload builder (used inside MediaStep's registered handler).
 */
/**
 * Returns true for real server IDs (small integers).
 * Date.now() temp IDs are ~1.7e12 and are excluded so new records don't send stale client IDs.
 */
export const isServerId = (id) => typeof id === "number" && id < 1e12

/**
 * Ticket Type step payload.
 */
export const buildTicketTypePayload = (formValues) => {
  const { ticket_types = [], ticket_duration, min_tickets, max_tickets, ticket_description } = formValues
  return serialize(
    {
      trip: {
        ticket_types_attributes: ticket_types.map(({ id, title, description, enabled, _destroy }) => {
          if (_destroy) return { id, _destroy: true }
          return {
            ...(isServerId(id) ? { id } : {}),
            title,
            description,
            enabled,
          }
        }),
        ticket_duration,
        min_tickets,
        max_tickets,
        ticket_description
      }
    },
    { allowEmptyArrays: true, indices: true }
  )
}

/**
 * Pricing step payload — payment type, base ticket prices, day/seasonal adjustments, fees.
 */
export const buildPricingPayload = (formValues) => {
  const {
    dayAdjustments = [],
    seasonalAdjustments = [],
    pricingFees = [],
    payment_type,
    deposit_percentage,
    ticketTypePrices = {},
    ticket_types = [],
  } = formValues
  const mapId = (id) => (isServerId(id) ? { id } : {})

  const ticket_type_prices_attributes = ticket_types
    .map(({ id }) => {
      const entry = ticketTypePrices[id]
      if (!entry) return null
      return {
        ...(isServerId(id) ? { ticket_type_id: id } : {}),
        price: entry.price,
        enabled: entry.enabled !== false,
      }
    })
    .filter(Boolean)

  const tripPayload = {
    wizard_step: "ticketed_pricing",
    payment_type,
    ...(payment_type === "deposit" ? { deposit_percent: deposit_percentage } : {}),
    ticket_type_prices_attributes,
  }

  if (dayAdjustments.length > 0) {
    tripPayload.day_adjustments_attributes = dayAdjustments.map(
      ({ id, name, appliesOn, customDays, direction, priceValue, priceType, affects, selectedTickets, _destroy }) => ({
        ...mapId(id),
        name,
        applies_on: appliesOn,
        // custom_days is only meaningful for the "custom" option — sending it for
        // weekdays/weekends would serialize an empty [""] the backend doesn't want.
        ...(appliesOn === "custom" ? { custom_days: customDays || [] } : {}),
        direction,
        price_value: priceValue,
        price_type: priceType,
        affects,
        // selected_ticket_ids only applies when targeting specific tickets;
        // private trips (affects: "all") have none.
        ...(affects === AFFECTS.SELECTED ? { selected_ticket_ids: selectedTickets || [] } : {}),
        ...(_destroy ? { _destroy: true } : {})
      })
    )
  }

  if (seasonalAdjustments.length > 0) {
    tripPayload.seasonal_adjustments_attributes = seasonalAdjustments.map(
      ({ id, name, startDate, endDate, isRepeatEachYear, direction, priceValue, priceType, affects, selectedTickets, _destroy }) => ({
        ...mapId(id),
        name,
        start_date: startDate?.format?.("YYYY-MM-DD") ?? startDate,
        end_date: endDate?.format?.("YYYY-MM-DD") ?? endDate,
        is_repeat_each_year: isRepeatEachYear ?? true,
        direction,
        price_value: priceValue,
        price_type: priceType,
        affects,
        selected_ticket_ids: selectedTickets || [],
        ...(_destroy ? { _destroy: true } : {})
      })
    )
  }

  tripPayload.trip_tax_fee_ids = pricingFees.map((fee) => fee.id)

  return serialize({ trip: tripPayload }, { allowEmptyArrays: true })
}

/**
 * Private listing Pricing step payload.
 * Sends trip_prices_attributes, pricing_type, payment info, and guest details.
 */
export const buildPrivatePricingPayload = ({ localPrices = [], serverPriceIds = [], dayAdjustments = [], seasonalAdjustments = [], pricingFees = [], formValues = {} }) => {
  const {
    pricing_type,
    payment_type,
    deposit_percentage,
    min_guests,
    max_guests,
    licenses_required,
    licenses_list,
    duration_type,
  } = formValues

  const isMultiDay = duration_type === "multi_day"
  const isFlatRate = pricing_type === PRICING_TYPES.FLAT_RATE
  // Flat rate: backend wants max_extra_guests (int) = max_guests − included_guests.
  const maxExtraGuests = (includedGuests) => Math.max(0, (max_guests ?? 0) - (includedGuests ?? 0))

  let trip_prices_attributes

  if (isMultiDay) {
    // Single price entry built from flat form fields — no trip_duration for multi-day
    const singlePrice = {
      pricing_type,
      trip_buffer_time: formValues.trip_buffer_time ?? null,
      price_per_person: formValues.price_per_person ?? null,
      price_per_child: formValues.price_per_child ?? null,
      price_per_infant: formValues.price_per_infant ?? null,
      price_per_passenger: formValues.price_per_passenger ?? null,
      flat_rate: formValues.flat_rate ?? null,
      included_guests: formValues.included_guests ?? null,
      adults_allowed: formValues.adults_allowed ?? true,
      children_allowed: formValues.children_allowed ?? false,
      infants_allowed: formValues.infants_allowed ?? false,
      passengers_allowed: formValues.passengers_allowed ?? false,
      ...(isFlatRate ? { max_extra_guests: maxExtraGuests(formValues.included_guests) } : {}),
    }
    if (isServerId(serverPriceIds[0])) singlePrice.id = serverPriceIds[0]
    const destroyExtras = serverPriceIds.slice(1).filter(isServerId).map((id) => ({ id, _destroy: true }))
    trip_prices_attributes = [singlePrice, ...destroyExtras]
  } else {
    // Booking options created under a different pricing model are invalid for
    // the current one — keep only options whose own pricing_type matches the
    // selected model (options with no stored pricing_type are treated as a match,
    // e.g. freshly added ones). Mismatched server records are dropped via _destroy.
    const matchingPrices = localPrices.filter(
      (price) => !price.pricing_type || price.pricing_type === pricing_type
    )
    const keptServerIds = new Set(
      matchingPrices.filter((price) => isServerId(price.id)).map((price) => price.id)
    )

    trip_prices_attributes = [
      ...matchingPrices.map(({ id, trip_duration, trip_buffer_time, price_per_person, price_per_child,
        price_per_infant, price_per_passenger, flat_rate, included_guests,
        adults_allowed, children_allowed, infants_allowed, passengers_allowed }) => ({
        ...(isServerId(id) ? { id } : {}),
        pricing_type,
        trip_duration,
        trip_buffer_time,
        adults_allowed: adults_allowed ?? true,
        children_allowed: children_allowed ?? false,
        infants_allowed: infants_allowed ?? false,
        passengers_allowed: passengers_allowed ?? false,
        // Emit only the fields relevant to the model so per-person prices don't
        // bleed into flat-rate options (and vice-versa).
        ...(isFlatRate
          ? {
              flat_rate,
              included_guests,
              price_per_person, // flat-rate: charge per additional guest
              max_extra_guests: maxExtraGuests(included_guests),
            }
          : {
              price_per_person,
              price_per_child,
              price_per_infant,
              price_per_passenger,
            }),
      })),
      // Destroy any server price no longer kept (removed or model-mismatched).
      ...serverPriceIds.filter((id) => !keptServerIds.has(id)).map((id) => ({ id, _destroy: true })),
    ]
  }

  const mapId = (id) => (isServerId(id) ? { id } : {})

  const tripPayload = {
    wizard_step: "pricing",
    pricing_type,
    payment_type,
    ...(payment_type === "deposit" ? { deposit_percent: deposit_percentage } : {}),
    min_guests,
    max_guests,
    licenses_required,
    ...(licenses_required ? { licenses_list } : {}),
    trip_prices_attributes,
  }

  if (dayAdjustments.length > 0) {
    tripPayload.day_adjustments_attributes = dayAdjustments
      // A never-saved adjustment that was deleted has nothing to remove — skip it.
      .filter((adjustment) => !(adjustment._destroy && !isServerId(adjustment.id)))
      .map((adjustment) => {
        // Persisted adjustment flagged for deletion: send only id + _destroy.
        if (adjustment._destroy && isServerId(adjustment.id)) {
          return { id: adjustment.id, _destroy: true }
        }
        const { id, name, appliesOn, customDays, direction, priceValue, priceType } = adjustment
        return {
          ...mapId(id),
          name,
          applies_on: appliesOn,
          // custom_days is only meaningful for the "custom" option — sending it for
          // weekdays/weekend would serialize an empty [""] the backend doesn't want.
          ...(appliesOn === "custom" ? { custom_days: customDays || [] } : {}),
          direction,
          price_value: priceValue,
          price_type: priceType,
          // Private trips apply adjustments to the whole base rate — affects /
          // selected_ticket_ids are omitted entirely.
        }
      })
  }

  if (seasonalAdjustments.length > 0) {
    tripPayload.seasonal_adjustments_attributes = seasonalAdjustments
      // A never-saved adjustment that was deleted has nothing to remove — skip it.
      .filter((adjustment) => !(adjustment._destroy && !isServerId(adjustment.id)))
      .map((adjustment) => {
        // Persisted adjustment flagged for deletion: send only id + _destroy.
        if (adjustment._destroy && isServerId(adjustment.id)) {
          return { id: adjustment.id, _destroy: true }
        }
        const { id, name, appliesOn, dateRanges, isRepeatEachYear, direction, priceValue, priceType } = adjustment
        const attributes = {
          ...mapId(id),
          name,
          applies_on_schedule_type: appliesOn,
          is_repeat_each_year: isRepeatEachYear ?? true,
          direction,
          price_value: priceValue,
          price_type: priceType,
          // Private trips apply to the whole base rate — omit affects / selected_ticket_ids.
        }

        // Custom dates carry explicit ranges as nested date_ranges_attributes.
        // default_schedule derives its dates from the Scheduling tab, so the
        // backend doesn't need (or want) date_ranges sent for it.
        if (appliesOn === APPLIES_ON_PRIVATE.CUSTOM_DATES) {
          attributes.date_ranges_attributes = (dateRanges || [])
            .filter((range) => (range.value?.[0] && range.value?.[1]) || (range._destroy && isServerId(range.id)))
            .map((range) => {
              if (range._destroy && isServerId(range.id)) {
                return { id: range.id, _destroy: true }
              }
              return {
                ...mapId(range.id),
                start_date: range.value[0].format("YYYY-MM-DD"),
                end_date: range.value[1].format("YYYY-MM-DD"),
              }
            })
        }

        return attributes
      }
    )
  }

  // indices: true is needed for the nested object arrays (trip_prices_attributes,
  // day/seasonal adjustments). trip_tax_fee_ids is a scalar array, though, and the
  // backend wants it un-indexed ([]), so append it manually after serialize.
  const formData = serialize({ trip: tripPayload }, { allowEmptyArrays: true, indices: true })
  if (pricingFees.length > 0) {
    pricingFees.forEach((fee) => formData.append("trip[trip_tax_fee_ids][]", fee.id))
  } else {
    formData.append("trip[trip_tax_fee_ids][]", "")
  }
  return formData
}

export const buildMediaPayload = (localMedia) =>
  serialize(
    {
      trip: {
        wizard_step: "media",
        media_attributes: localMedia.map(({ id, file_type, blob, _destroy, position, file }) => ({
          file_type,
          _destroy,
          position,
          ...(file ? { file } : { id }),
          ...(file_type === "video" || file_type === "trimmed_video" ? { preview: blob } : {})
        }))
      }
    },
    { indices: true }
  )

/**
 * Additional Info step — payload for waivers and question templates.
 * (Add-ons moved to the Details step as a selector — see buildDetailsPayload's add_on_ids.)
 *
 * @param {Array}  digitalWaiverIds      - Selected waiver IDs from form field "digital_waiver"
 * @param {Array}  questionTemplateIds   - Selected template IDs from form field "question_template"
 */
export const buildAdditionalInfoPayload = ({ selectedWaiverIds = [], existingWaiverJoins = [], questionTemplateId = null }) => {
  // digital_waiver_experiences is a join model. The backend wants nested attributes:
  //   - newly selected waiver  → { digital_waiver_id }
  //   - de-selected existing    → { id: <join id>, _destroy: true }
  //   - unchanged               → omitted (left untouched)
  const existingJoinByWaiverId = new Map(existingWaiverJoins.map((join) => [join.digital_waiver_id, join.id]))
  const selectedSet = new Set(selectedWaiverIds)

  const waiverAttributes = []
  selectedWaiverIds.forEach((waiverId) => {
    if (!existingJoinByWaiverId.has(waiverId)) {
      waiverAttributes.push({ digital_waiver_id: waiverId })
    }
  })
  existingWaiverJoins.forEach((join) => {
    if (!selectedSet.has(join.digital_waiver_id)) {
      waiverAttributes.push({ id: join.id, _destroy: true })
    }
  })

  // indices: true groups each waiver join entry ([i][id]/[i][_destroy]/[i][digital_waiver_id]).
  return serialize(
    {
      trip: {
        wizard_step: "additional_info",
        ...(waiverAttributes.length > 0 ? { digital_waiver_experiences_attributes: waiverAttributes } : {}),
        // Questions is single-select — send a scalar template id (or "" to clear).
        question_template_id: questionTemplateId ?? ""
      }
    },
    { allowEmptyArrays: true, indices: true }
  )
}
